import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, authStorage, onUnauthorized } from "./api";

const AuthContext = createContext(null);

const STATES = {
  CHECKING: "checking",
  AUTHED: "authed",
  ANON: "anon",
};

export function AuthProvider({ children }) {
  const [status, setStatus] = useState(STATES.CHECKING);
  const [username, setUsername] = useState(null);

  const refresh = useCallback(async () => {
    const token = authStorage.getToken();
    if (!token) {
      setStatus(STATES.ANON);
      setUsername(null);
      return;
    }
    try {
      const { data } = await api.get("/api/auth/me");
      setUsername(data.username);
      setStatus(STATES.AUTHED);
    } catch {
      authStorage.clear();
      setStatus(STATES.ANON);
      setUsername(null);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Listen for global 401 and downgrade to anon.
  useEffect(() => {
    const unsub = onUnauthorized(() => {
      setStatus(STATES.ANON);
      setUsername(null);
    });
    return () => unsub();
  }, []);

  const login = useCallback(async (u, p) => {
    const { data } = await api.post("/api/auth/login", { username: u, password: p });
    authStorage.set(data.token, data.username, data.expires_at);
    setUsername(data.username);
    setStatus(STATES.AUTHED);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch {
      // ignore — clear locally regardless
    }
    authStorage.clear();
    setStatus(STATES.ANON);
    setUsername(null);
  }, []);

  const value = {
    status,
    isChecking: status === STATES.CHECKING,
    isAuthed: status === STATES.AUTHED,
    username,
    login,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
